import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ReactFlow, Background, Controls, MiniMap, addEdge, applyNodeChanges, applyEdgeChanges,
  Handle, Position, MarkerType,
  type Node, type Edge, type Connection, type NodeChange, type EdgeChange,
  type NodeProps,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import Dagre from '@dagrejs/dagre';
import {
  Card, Modal, ConfirmDialog, FormField, TextInput, TextArea, FormSelect,
  StatusBadge, PageHeader, DataTable, FilterBar, EmptyState, Icon,
  type DataTableColumn,
} from '@/components/ui';
import {
  superApi,
  type VoiceFlowRow, type VoiceFlowGraph, type VoiceFlowStage, type VoiceFlowTransition,
  type VoiceStageType, type VoiceStageVariable, type VoiceTool,
  type VoiceFlowStagePreview, type VoiceSimTurn, type VoiceSimEvent, type VoiceFlowStats,
  type VoiceAgentRow, type VoiceTransitionGuard, type VoiceGuardOp,
} from '@/shared/api/superApi';
import { toast } from '@/components/ui/Toast';
import { VoiceToolForm } from './VoiceTools';

interface TenantLite { id: number; name: string; slug: string }

// ── פונקציות מובנות שאפשר להתיר בשלב ─────────────────────────────────────────
const BUILTIN_TOOLS = ['transfer_to_human', 'schedule_callback', 'send_sms', 'lookup_order', 'end_call', 'save_note'];
// משתני מערכת זמינים תמיד (לעוזר המשתנים ולסריקת {{unknown}}).
const SYSTEM_VARIABLES = ['caller_number', 'callee_number', 'call_direction', 'agent_name', 'now', 'day_of_week'];
// סוגי שלב סופיים — מותר להם לא לצאת החוצה.
const TERMINAL_TYPES: VoiceStageType[] = ['end_call', 'transfer'];

// ── תנאי-משתנה (guard) על מעבר ────────────────────────────────────────────────
const GUARD_OPS: { value: VoiceGuardOp; label: string }[] = [
  { value: 'eq', label: 'שווה' },
  { value: 'ne', label: 'לא שווה' },
  { value: 'contains', label: 'מכיל' },
  { value: 'not_contains', label: 'לא מכיל' },
  { value: 'exists', label: 'קיים' },
  { value: 'not_exists', label: 'לא קיים' },
  { value: 'gt', label: 'גדול מ' },
  { value: 'lt', label: 'קטן מ' },
  { value: 'in', label: 'אחד מ' },
];
// אופרטורים ללא ערך.
const VALUELESS_OPS: VoiceGuardOp[] = ['exists', 'not_exists'];

// תקציר קריא של guard לתווית הקשת (למשל "type=vip", "amount>100").
function guardSummary(g: VoiceTransitionGuard): string {
  const sym: Partial<Record<VoiceGuardOp, string>> = { eq: '=', ne: '≠', gt: '>', lt: '<', contains: '⊇', not_contains: '⊉', in: '∈' };
  if (g.op === 'exists') return `${g.variable}?`;
  if (g.op === 'not_exists') return `!${g.variable}`;
  const v = Array.isArray(g.value) ? g.value.join(',') : (g.value ?? '');
  return `${g.variable}${sym[g.op] ?? ' '}${v}`;
}

// ── מפת סוגי שלב: צבע, תווית, אייקון ─────────────────────────────────────────
const STAGE_META: Record<VoiceStageType, { label: string; color: string; icon: string }> = {
  start: { label: 'התחלה', color: 'var(--ok, #16a34a)', icon: 'play' },
  normal: { label: 'רגיל', color: 'var(--accent)', icon: 'chat' },
  function: { label: 'פונקציה', color: '#8b5cf6', icon: 'bolt' },
  transfer: { label: 'העברה', color: '#f59e0b', icon: 'transfer' },
  end_call: { label: 'סיום שיחה', color: 'var(--danger)', icon: 'hang' },
};
const STAGE_TYPES: VoiceStageType[] = ['start', 'normal', 'function', 'transfer', 'end_call'];

// ── תבניות זרימה למודל היצירה ─────────────────────────────────────────────────
type FlowTemplate = { id: string; label: string; build?: () => { stages: VoiceFlowStage[]; transitions: VoiceFlowTransition[] } };
function stg(key: string, name: string, type: VoiceStageType, i: number, extra?: Partial<VoiceFlowStage>): VoiceFlowStage {
  return {
    key, name, type, prompt: '', allowed_tools: [], collect_variables: [],
    canvas: { x: 80 + i * 260, y: 120 }, ...extra,
  };
}
const FLOW_TEMPLATES: FlowTemplate[] = [
  { id: 'blank', label: 'ריק' },
  {
    id: 'intake', label: 'פתיחת פנייה',
    build: () => ({
      stages: [
        stg('opening', 'פתיחה', 'start', 0),
        stg('collect', 'איסוף פרטים', 'normal', 1, { collect_variables: [
          { name: 'name', type: 'string', required: true }, { name: 'topic', type: 'string', required: true },
        ] }),
        stg('confirm', 'אישור', 'normal', 2),
        stg('done', 'סיום', 'end_call', 3),
      ],
      transitions: [
        { from: 'opening', to: 'collect', condition_description: 'הלקוח מוכן להתחיל' },
        { from: 'collect', to: 'confirm', condition_description: 'הפרטים נאספו' },
        { from: 'confirm', to: 'done', condition_description: 'הלקוח אישר את הפרטים' },
      ],
    }),
  },
  {
    id: 'order_branch', label: 'הזמנה עם פיצול (IF) ושליחת API',
    build: () => ({
      stages: [
        stg('opening', 'פתיחה', 'start', 0, { prompt: 'ברך את הלקוח והצע לבצע הזמנה.' }),
        stg('order_type_ask', 'בשר או חלב?', 'normal', 1, {
          prompt: 'שאל: "תרצה להזמין בשר או חלב?" ושמור את התשובה במשתנה order_type.',
          collect_variables: [{ name: 'order_type', type: 'string', description: 'בשר או חלב', required: true }],
        }),
        stg('order_type_meat', 'הזמנת בשר', 'normal', 2, { prompt: 'קבל את פרטי הזמנת הבשר.', canvas: { x: 80 + 2 * 260, y: 40 } }),
        stg('order_type_dairy', 'הזמנת חלב', 'normal', 2, { prompt: 'קבל את פרטי הזמנת החלב.', canvas: { x: 80 + 2 * 260, y: 210 } }),
        stg('send_api', 'שליחת ההזמנה (בחר כלי API)', 'function', 3, { canvas: { x: 80 + 3 * 260, y: 120 } }),
        stg('done', 'סיום', 'end_call', 4),
      ],
      transitions: [
        { from: 'opening', to: 'order_type_ask', condition_description: 'הלקוח מוכן להזמין' },
        { from: 'order_type_ask', to: 'order_type_meat', condition_description: 'order_type = בשר', guard: { variable: 'order_type', op: 'in', value: ['בשר'] } },
        { from: 'order_type_ask', to: 'order_type_dairy', condition_description: 'order_type = חלב', guard: { variable: 'order_type', op: 'in', value: ['חלב'] } },
        { from: 'order_type_meat', to: 'send_api', condition_description: 'ההזמנה הושלמה' },
        { from: 'order_type_dairy', to: 'send_api', condition_description: 'ההזמנה הושלמה' },
        { from: 'send_api', to: 'done', condition_description: 'לאחר שליחת ה-API' },
      ],
    }),
  },
  {
    id: 'appointment', label: 'תיאום תור',
    build: () => ({
      stages: [
        stg('opening', 'פתיחה', 'start', 0),
        stg('collect', 'איסוף פרטים', 'normal', 1, { collect_variables: [
          { name: 'name', type: 'string', required: true }, { name: 'requested_time', type: 'string', required: true },
        ] }),
        stg('confirm', 'אישור התור', 'normal', 2),
        stg('done', 'סיום', 'end_call', 3),
      ],
      transitions: [
        { from: 'opening', to: 'collect', condition_description: 'הלקוח רוצה לתאם תור' },
        { from: 'collect', to: 'confirm', condition_description: 'הפרטים נאספו' },
        { from: 'confirm', to: 'done', condition_description: 'הלקוח אישר את התור' },
      ],
    }),
  },
  {
    id: 'routing', label: 'ניתוב מחלקות',
    build: () => ({
      stages: [
        stg('opening', 'פתיחה', 'start', 0),
        stg('transfer_sales', 'העברה למכירות', 'transfer', 1, { transfer_target: 'sales' }),
        stg('transfer_support', 'העברה לתמיכה', 'transfer', 2, { transfer_target: 'support' }),
      ],
      transitions: [
        { from: 'opening', to: 'transfer_sales', condition_description: 'הלקוח מעוניין ברכישה' },
        { from: 'opening', to: 'transfer_support', condition_description: 'הלקוח צריך תמיכה' },
      ],
    }),
  },
];

// ── Page: list ↔ builder ─────────────────────────────────────────────────────
export function VoiceFlows() {
  const [tenants, setTenants] = useState<TenantLite[]>([]);
  const [builderId, setBuilderId] = useState<number | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const t = await superApi.tenants({ per_page: 500 });
        const items = (t as { data?: unknown[] }).data ?? [];
        setTenants(items.map((x) => x as TenantLite).filter((x) => x && typeof x.id === 'number'));
      } catch { /* non-fatal */ }
    })();
  }, []);

  const tenantName = useCallback(
    (id?: number | null) => (id == null ? null : tenants.find((t) => t.id === id)?.name ?? `#${id}`),
    [tenants],
  );

  if (builderId != null) {
    return <FlowBuilder flowId={builderId} onBack={() => setBuilderId(null)} />;
  }
  return <FlowsList tenants={tenants} tenantName={tenantName} onOpen={setBuilderId} />;
}

// ── Flows list ────────────────────────────────────────────────────────────────
function FlowsList({ tenants, tenantName, onOpen }: {
  tenants: TenantLite[];
  tenantName: (id?: number | null) => string | null;
  onOpen: (id: number) => void;
}) {
  const [rows, setRows] = useState<VoiceFlowRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<VoiceFlowRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<VoiceFlowRow | null>(null);
  const [duplicating, setDuplicating] = useState<number | null>(null);

  const reqSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++reqSeq.current;
    setLoading(true);
    try {
      const res = await superApi.voiceFlows({ search: search || undefined, per_page: 100 });
      if (seq !== reqSeq.current) return;
      setRows(res.data);
    } catch {
      if (seq === reqSeq.current) toast('שגיאה בטעינת הזרימות', 'danger');
    } finally {
      if (seq === reqSeq.current) setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const h = setTimeout(() => void load(), 250);
    return () => clearTimeout(h);
  }, [load]);

  // שכפול זרימה: יוצר זרימה חדשה "(עותק)" ואז שומר לתוכה את אותו הגרף.
  async function duplicateFlow(flow: VoiceFlowRow) {
    setDuplicating(flow.id);
    try {
      const graph = await superApi.voiceFlowGraph(flow.id);
      const created = await superApi.createVoiceFlow({
        tenant_id: flow.tenant_id ?? null,
        name: `${flow.name} (עותק)`,
        description: flow.description ?? null,
      });
      await superApi.saveVoiceFlowGraph(created.id, { stages: graph.stages, transitions: graph.transitions });
      toast('הזרימה שוכפלה', 'ok');
      void load();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'שגיאה בשכפול';
      toast(msg, 'danger');
    } finally {
      setDuplicating(null);
    }
  }

  const columns: DataTableColumn<VoiceFlowRow>[] = useMemo(() => [
    { key: 'name', he: 'שם', render: (r) => <strong>{r.name}</strong> },
    {
      key: 'scope', he: 'שיוך', render: (r) =>
        r.tenant_id
          ? <StatusBadge tone="neutral" size="sm">מרכזיה · {tenantName(r.tenant_id)}</StatusBadge>
          : <StatusBadge tone="ok" size="sm">גלובלי</StatusBadge>,
    },
    { key: 'stages_count', he: 'שלבים', align: 'center', render: (r) => r.stages_count ?? 0 },
    {
      key: 'agents_count', he: 'בשימוש', align: 'center',
      render: (r) => (r.agents_count
        ? <StatusBadge tone="info" size="sm">{r.agents_count} סוכנים</StatusBadge>
        : <span style={{ color: 'var(--muted)', fontSize: 'var(--t-xs)' }}>—</span>),
    },
    {
      key: 'actions', he: '', align: 'center',
      render: (r) => (
        <div style={{ display: 'inline-flex', gap: 6 }}>
          <button type="button" className="btn primary" style={{ height: 30 }} onClick={(e) => { e.stopPropagation(); onOpen(r.id); }}>
            פתח בונה
          </button>
          <button type="button" className="btn" style={{ height: 30 }} onClick={(e) => { e.stopPropagation(); setEditTarget(r); }}>
            שנה שם
          </button>
          <button type="button" className="btn" style={{ height: 30 }} disabled={duplicating === r.id} onClick={(e) => { e.stopPropagation(); void duplicateFlow(r); }}>
            {duplicating === r.id ? 'משכפל…' : 'שכפל'}
          </button>
          <button type="button" className="btn danger" style={{ height: 30 }} onClick={(e) => { e.stopPropagation(); setDeleteTarget(r); }}>
            מחק
          </button>
        </div>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [tenantName, onOpen, duplicating]);

  async function handleDelete(flow: VoiceFlowRow) {
    try {
      await superApi.deleteVoiceFlow(flow.id);
      toast('הזרימה נמחקה', 'ok');
      setDeleteTarget(null);
      setRows((prev) => prev.filter((r) => r.id !== flow.id));
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'שגיאה במחיקה';
      toast(msg, 'danger');
    }
  }

  return (
    <div className="stack" style={{ padding: 'var(--gap)' }}>
      <PageHeader
        title="זרימות קול"
        subtitle="בונה זרימות שיחה לסוכני קול — שלבים, מעברים ואיסוף משתנים"
        actions={
          <button type="button" className="btn primary" onClick={() => setCreateOpen(true)}>
            + זרימה חדשה
          </button>
        }
      />
      <FilterBar search={search} onSearch={setSearch} onClear={() => setSearch('')} placeholder="חיפוש לפי שם…" />
      <Card>
        <DataTable
          columns={columns}
          rows={rows}
          loading={loading}
          onRowClick={(r) => onOpen(r.id)}
          caption="זרימות קול"
          emptyState={
            <EmptyState
              title="אין זרימות עדיין"
              description="צור זרימה ראשונה כדי לבנות מסלול שיחה מובנה לסוכן קול."
              action={<button type="button" className="btn primary" onClick={() => setCreateOpen(true)}>+ זרימה חדשה</button>}
            />
          }
        />
      </Card>

      {createOpen && (
        <FlowMetaForm
          mode="create"
          tenants={tenants}
          onClose={() => setCreateOpen(false)}
          onSaved={(flow) => { setCreateOpen(false); void load(); if (flow) onOpen(flow.id); }}
        />
      )}
      {editTarget && (
        <FlowMetaForm
          mode="edit"
          flow={editTarget}
          tenants={tenants}
          onClose={() => setEditTarget(null)}
          onSaved={() => { setEditTarget(null); void load(); }}
        />
      )}
      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && handleDelete(deleteTarget)}
        title="מחיקת זרימה"
        message={deleteTarget ? `למחוק את "${deleteTarget.name}"? אם היא מקושרת לסוכנים המחיקה תיחסם.` : ''}
        confirmLabel="מחק"
      />
    </div>
  );
}

export function FlowMetaForm({ mode, flow, tenants, onClose, onSaved }: {
  mode: 'create' | 'edit';
  flow?: VoiceFlowRow;
  tenants: TenantLite[];
  onClose: () => void;
  onSaved: (flow?: VoiceFlowRow) => void;
}) {
  const [name, setName] = useState(flow?.name ?? '');
  const [description, setDescription] = useState(flow?.description ?? '');
  const [tenantId, setTenantId] = useState(flow?.tenant_id != null ? String(flow.tenant_id) : '');
  const [template, setTemplate] = useState('blank');
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!name.trim()) { toast('יש למלא שם', 'danger'); return; }
    setSaving(true);
    try {
      if (mode === 'create') {
        const created = await superApi.createVoiceFlow({
          tenant_id: tenantId ? Number(tenantId) : null,
          name: name.trim(),
          description: description.trim() || null,
        });
        // תבנית נבחרה → שמור את הגרף שלה מיד.
        const tpl = FLOW_TEMPLATES.find((t) => t.id === template);
        if (tpl?.build) {
          try { await superApi.saveVoiceFlowGraph(created.id, tpl.build()); } catch { /* לא חוסם — נפתח ריק */ }
        }
        toast('הזרימה נוצרה', 'ok');
        onSaved(created);
      } else if (flow) {
        await superApi.updateVoiceFlow(flow.id, {
          name: name.trim(),
          description: description.trim() || null,
          tenant_id: tenantId ? Number(tenantId) : null,
        });
        toast('עודכן', 'ok');
        onSaved();
      }
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'שגיאה בשמירה';
      toast(msg, 'danger');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="sm"
      title={mode === 'create' ? 'זרימה חדשה' : `עריכת ${flow?.name}`}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={saving}>ביטול</button>
          <button type="button" className="btn primary" onClick={save} disabled={saving}>
            {saving ? 'שומר…' : 'שמור'}
          </button>
        </>
      }
    >
      <div className="stack">
        <FormField label="שם" required>
          <TextInput value={name} onChange={(e) => setName(e.target.value)} />
        </FormField>
        <FormField label="תיאור">
          <TextArea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </FormField>
        <FormField label="שיוך" hint="ריק = זרימה גלובלית לכל המרכזיות">
          <FormSelect value={tenantId} onChange={(e) => setTenantId(e.target.value)}>
            <option value="">גלובלי (משותף)</option>
            {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </FormSelect>
        </FormField>
        {mode === 'create' && (
          <FormField label="תבנית" hint="נקודת פתיחה — אפשר לערוך הכל אחר-כך בבונה">
            <FormSelect value={template} onChange={(e) => setTemplate(e.target.value)}>
              {FLOW_TEMPLATES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </FormSelect>
          </FormField>
        )}
      </div>
    </Modal>
  );
}

// ── Builder ───────────────────────────────────────────────────────────────────
type StageNodeData = { stage: VoiceFlowStage; issue?: 'error' | 'warn' | null; stat?: { entered: number; dropped: number } | null };
type FlowNode = Node<StageNodeData, 'stage'>;

function StageNode({ data, selected }: NodeProps<FlowNode>) {
  const meta = STAGE_META[data.stage.type] ?? STAGE_META.normal;
  const isStart = data.stage.type === 'start';
  const issueBorder = data.issue === 'error'
    ? 'color-mix(in oklch, var(--danger) 45%, var(--line))'
    : data.issue === 'warn'
      ? 'color-mix(in oklch, var(--warn, #f59e0b) 55%, var(--line))'
      : 'var(--line)';
  const handleStyle: React.CSSProperties = {
    width: 14, height: 14, background: 'var(--surface)',
    border: '2px solid var(--line)', borderRadius: '50%',
  };
  return (
    <div
      className="vf-node"
      style={{
        width: 250, borderRadius: 16,
        background: 'color-mix(in oklch, var(--surface) 92%, transparent)',
        backdropFilter: 'blur(4px)',
        border: `2px solid ${selected ? 'var(--accent)' : issueBorder}`,
        boxShadow: selected ? '0 0 0 4px color-mix(in oklch, var(--accent) 22%, transparent)' : 'var(--shadow-1)',
      }}
    >
      {isStart && (
        <div style={{
          position: 'absolute', top: -11, insetInlineStart: 14, zIndex: 3,
          background: 'var(--text)', color: 'var(--surface)',
          fontSize: 10, fontWeight: 700, padding: '2px 10px',
          borderRadius: 'var(--r-pill)', boxShadow: 'var(--shadow-2)',
        }}>
          נקודת התחלה
        </div>
      )}
      <Handle type="target" position={Position.Left} style={handleStyle} />
      <div style={{ padding: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <div style={{
            padding: 9, borderRadius: 12, flexShrink: 0, display: 'flex',
            background: `color-mix(in oklch, ${meta.color} 14%, var(--surface))`,
            color: meta.color,
          }}>
            <Icon name={meta.icon} size={18} color={meta.color} />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {data.stage.name || data.stage.key}
            </div>
            <div style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 500 }}>
              {meta.label} · <span dir="ltr">{data.stage.key}</span>
            </div>
          </div>
        </div>

        {/* פס חיווי תחתון: תקלה בשלב / סטטיסטיקה / שקט */}
        {data.issue ? (
          <div style={{
            marginTop: 10, display: 'flex', alignItems: 'center', gap: 8,
            fontSize: 11, fontWeight: 600,
            color: data.issue === 'error' ? 'var(--danger)' : 'var(--warn, #f59e0b)',
            background: data.issue === 'error'
              ? 'color-mix(in oklch, var(--danger) 8%, var(--surface))'
              : 'color-mix(in oklch, var(--warn, #f59e0b) 10%, var(--surface))',
            border: `1px solid ${data.issue === 'error' ? 'color-mix(in oklch, var(--danger) 25%, transparent)' : 'color-mix(in oklch, var(--warn, #f59e0b) 30%, transparent)'}`,
            borderRadius: 10, padding: '7px 10px',
          }}>
            <span style={{
              width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
              background: data.issue === 'error' ? 'var(--danger)' : 'var(--warn, #f59e0b)',
              animation: 'vf-pulse 1.2s ease-in-out infinite',
            }} />
            {data.issue === 'error' ? 'שגיאה בשלב — ראה אזהרות' : 'אזהרה בשלב'}
          </div>
        ) : data.stat ? (
          <div style={{
            marginTop: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
            fontSize: 11, fontWeight: 500, color: 'var(--text-2)',
            background: 'color-mix(in oklch, var(--surface-2) 60%, transparent)',
            border: '1px solid var(--line)', borderRadius: 10, padding: '7px 10px',
          }}>
            <span>נכנסו: {data.stat.entered}</span>
            {data.stat.dropped > 0 && <span style={{ color: 'var(--danger)', fontWeight: 700 }}>נטשו: {data.stat.dropped}</span>}
          </div>
        ) : null}
      </div>
      <Handle type="source" position={Position.Right} style={handleStyle} />
    </div>
  );
}

const NODE_TYPES = { stage: StageNode };

function slugify(base: string, existing: Set<string>): string {
  const root = (base || 'stage').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'stage';
  if (!existing.has(root)) return root;
  let i = 2;
  while (existing.has(`${root}_${i}`)) i += 1;
  return `${root}_${i}`;
}

// ── לוגיקת ולידציה בצד-לקוח (משקפת את השרת; השרת נשאר סמכותי בשמירה) ──────────
function computeIssues(nodes: FlowNode[], edges: Edge[], knownVariables: string[]): { warnings: string[]; nodeIssue: Record<string, 'error' | 'warn'> } {
  const warnings: string[] = [];
  const nodeIssue: Record<string, 'error' | 'warn'> = {};
  if (nodes.length === 0) return { warnings, nodeIssue };

  const starts = nodes.filter((n) => n.data.stage.type === 'start');
  if (starts.length === 0) warnings.push('אין שלב התחלה (start)');
  if (starts.length > 1) { warnings.push('יש יותר משלב התחלה אחד'); starts.forEach((s) => { nodeIssue[s.data.stage.key] = 'error'; }); }

  // מפתחות כפולים
  const seen = new Set<string>();
  for (const n of nodes) {
    const k = n.data.stage.key;
    if (seen.has(k)) { warnings.push(`מפתח כפול: ${k}`); nodeIssue[k] = 'error'; }
    seen.add(k);
  }

  // שלבים לא נגישים (BFS מה-start)
  const adj = new Map<string, string[]>();
  for (const e of edges) { adj.set(e.source, [...(adj.get(e.source) ?? []), e.target]); }
  const reachable = new Set<string>();
  const queue = starts.map((s) => s.data.stage.key);
  while (queue.length) {
    const cur = queue.shift()!;
    if (reachable.has(cur)) continue;
    reachable.add(cur);
    for (const next of adj.get(cur) ?? []) queue.push(next);
  }
  for (const n of nodes) {
    if (!reachable.has(n.data.stage.key) && n.data.stage.type !== 'start') {
      warnings.push(`שלב לא נגיש: ${n.data.stage.key}`);
      nodeIssue[n.data.stage.key] = nodeIssue[n.data.stage.key] ?? 'warn';
    }
  }

  // שלבים לא-סופיים בלי מעבר יוצא
  const hasOut = new Set(edges.map((e) => e.source));
  for (const n of nodes) {
    if (!TERMINAL_TYPES.includes(n.data.stage.type) && !hasOut.has(n.data.stage.key)) {
      warnings.push(`שלב ללא מעבר יוצא: ${n.data.stage.key}`);
      nodeIssue[n.data.stage.key] = nodeIssue[n.data.stage.key] ?? 'warn';
    }
  }

  // guard שמפנה למשתנה שאף אחד לא מייצר
  const guardVarsSeen = new Set<string>();
  for (const e of edges) {
    const g = e.data?.guard as VoiceTransitionGuard | undefined;
    const v = g?.variable?.trim();
    if (v && !knownVariables.includes(v) && !guardVarsSeen.has(v)) {
      guardVarsSeen.add(v);
      warnings.push(`תנאי מעבר משתמש במשתנה לא מוכר: ${v} — ודא שמשהו מייצר אותו`);
    }
  }

  return { warnings, nodeIssue };
}

export function FlowBuilder({ flowId, onBack }: { flowId: number; onBack: () => void }) {
  const [meta, setMeta] = useState<VoiceFlowGraph['flow'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [nodes, setNodes] = useState<FlowNode[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [selectedNodeKey, setSelectedNodeKey] = useState<string | null>(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
  const [banner, setBanner] = useState<{ errors: string[]; warnings: string[] } | null>(null);
  const [varsOpen, setVarsOpen] = useState(false);
  const [ifOpen, setIfOpen] = useState(false);
  const [endApiOpen, setEndApiOpen] = useState(false);
  const [issuesOpen, setIssuesOpen] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [previewKey, setPreviewKey] = useState<string | null>(null);
  const [simOpen, setSimOpen] = useState(false);
  const [statsDays, setStatsDays] = useState<number | null>(null); // null = כבוי
  const [stats, setStats] = useState<VoiceFlowStats | null>(null);
  const [confirmBack, setConfirmBack] = useState(false);

  const [libraryTools, setLibraryTools] = useState<VoiceTool[]>([]);
  const [builderTenants, setBuilderTenants] = useState<TenantLite[]>([]);
  const reloadLibraryTools = useCallback(async () => {
    try { setLibraryTools((await superApi.voiceToolsLibrary({ per_page: 200 })).data); } catch { /* non-fatal */ }
  }, []);
  const [flowAgents, setFlowAgents] = useState<VoiceAgentRow[]>([]);

  // Undo/redo history של snapshots {nodes, edges}. cap 50.
  const history = useRef<{ nodes: FlowNode[]; edges: Edge[] }[]>([]);
  const future = useRef<{ nodes: FlowNode[]; edges: Edge[] }[]>([]);
  const [histLen, setHistLen] = useState(0);
  const [futLen, setFutLen] = useState(0);

  // כלים לבחירה בשלב פונקציה / allowed_tools: מובנים + כלי ספרייה.
  const toolNameOptions = useMemo(
    () => [...BUILTIN_TOOLS, ...libraryTools.map((t) => t.name)],
    [libraryTools],
  );

  useEffect(() => {
    void (async () => {
      try { setLibraryTools((await superApi.voiceToolsLibrary({ per_page: 200 })).data); } catch { /* non-fatal */ }
      try {
        const t = await superApi.tenants({ per_page: 500 });
        setBuilderTenants((((t as { data?: unknown[] }).data ?? []) as TenantLite[]).filter((x) => x && typeof x.id === 'number'));
      } catch { /* non-fatal */ }
      try {
        const all = (await superApi.voiceAgents({ per_page: 200 })).data;
        setFlowAgents(all.filter((a) => a.flow_id === flowId));
      } catch { /* non-fatal */ }
    })();
  }, [flowId]);

  // ── Load graph ──
  useEffect(() => {
    let active = true;
    setLoading(true);
    void superApi.voiceFlowGraph(flowId)
      .then((g) => {
        if (!active) return;
        setMeta(g.flow);
        setNodes(g.stages.map((s, i) => stageToNode(s, i)));
        setEdges(g.transitions.map(transitionToEdge));
        setDirty(false);
        history.current = []; future.current = [];
        setHistLen(0); setFutLen(0);
      })
      .catch(() => { if (active) toast('שגיאה בטעינת הזרימה', 'danger'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [flowId]);

  const existingKeys = useMemo(() => new Set(nodes.map((n) => n.data.stage.key)), [nodes]);

  // צילום מצב לפני פעולה בדידה — לבסיס undo. מסמן dirty.
  const snapshot = useCallback(() => {
    history.current.push({ nodes, edges });
    if (history.current.length > 50) history.current.shift();
    future.current = [];
    setHistLen(history.current.length); setFutLen(0);
    setDirty(true);
  }, [nodes, edges]);

  function undo() {
    const prev = history.current.pop();
    if (!prev) return;
    future.current.push({ nodes, edges });
    setNodes(prev.nodes); setEdges(prev.edges);
    setHistLen(history.current.length); setFutLen(future.current.length);
    setDirty(true);
  }
  function redo() {
    const next = future.current.pop();
    if (!next) return;
    history.current.push({ nodes, edges });
    setNodes(next.nodes); setEdges(next.edges);
    setHistLen(history.current.length); setFutLen(future.current.length);
    setDirty(true);
  }

  // ── React Flow change handlers ──
  const onNodesChange = useCallback((changes: NodeChange<FlowNode>[]) => {
    // צילום פעם אחת בתחילת גרירה (dragging הופך מ-false ל-true), לא בכל פריים.
    const dragStart = changes.some((c) => c.type === 'position' && c.dragging === false);
    if (dragStart) snapshot();
    setNodes((nds) => applyNodeChanges(changes, nds));
  }, [snapshot]);
  const onEdgesChange = useCallback((changes: EdgeChange[]) => setEdges((eds) => applyEdgeChanges(changes, eds)), []);

  // חיבור קשת: יוצר מעבר עם תנאי ריק ובוחר אותו מיד לעריכה בפאנל.
  const onConnect = useCallback((c: Connection) => {
    if (!c.source || !c.target) return;
    snapshot();
    const id = `${c.source}__${c.target}`;
    setEdges((eds) => addEdge(
      { ...c, id, label: undefined, data: { condition_description: '' }, markerEnd: { type: MarkerType.ArrowClosed } },
      eds,
    ));
    setSelectedEdgeId(id);
    setSelectedNodeKey(null);
  }, [snapshot]);

  // ── Add stage ──
  function addStage() {
    snapshot();
    const key = slugify('stage', existingKeys);
    const isFirst = nodes.length === 0;
    const stage: VoiceFlowStage = {
      key, name: isFirst ? 'התחלה' : 'שלב חדש', type: isFirst ? 'start' : 'normal',
      prompt: '', allowed_tools: [], collect_variables: [],
      canvas: { x: 80 + (nodes.length % 4) * 240, y: 80 + Math.floor(nodes.length / 4) * 160 },
    };
    setNodes((nds) => [...nds, stageToNode(stage, nds.length)]);
    setSelectedNodeKey(key);
    setSelectedEdgeId(null);
  }

  /**
   * פיצול IF: שלב-שאלה שאוסף משתנה + ענף לכל תשובה, עם guard על כל קשת
   * (למשל "תרצה להזמין בשר או חלב?" → order_type=בשר / order_type=חלב).
   */
  function addIfSplit(question: string, variable: string, values: string[]) {
    snapshot();
    const used = new Set(existingKeys);
    const askKey = slugify(`${variable}_ask`, used); used.add(askKey);
    const baseX = nodes.length ? Math.max(...nodes.map((n) => n.position.x)) + 300 : 80;
    const baseY = nodes.length ? Math.min(...nodes.map((n) => n.position.y)) + 60 : 120;

    const askStage: VoiceFlowStage = {
      key: askKey, name: question.length > 28 ? `${question.slice(0, 28)}…` : question, type: 'normal',
      prompt: `שאל את הלקוח: "${question}" ושמור את תשובתו במשתנה ${variable} (save_variable).`,
      allowed_tools: [], collect_variables: [{ name: variable, type: 'string', description: question, required: true }],
      canvas: { x: baseX, y: baseY },
    };

    const newNodes: FlowNode[] = [stageToNode(askStage, 0)];
    const newEdges: Edge[] = [];

    if (selectedNodeKey && selectedNodeKey !== askKey) {
      newEdges.push({
        id: `${selectedNodeKey}__${askKey}`, source: selectedNodeKey, target: askKey,
        data: { condition_description: '' }, markerEnd: { type: MarkerType.ArrowClosed },
      });
    }

    values.forEach((v, i) => {
      const bKey = slugify(`${variable}_${v}`, used); used.add(bKey);
      const branch: VoiceFlowStage = {
        key: bKey, name: v, type: 'normal',
        prompt: `הלקוח בחר: ${v}. המשך את השיחה בהתאם.`,
        allowed_tools: [], collect_variables: [],
        canvas: { x: baseX + 300, y: baseY + i * 170 - ((values.length - 1) * 85) },
      };
      newNodes.push(stageToNode(branch, 0));
      const guard = { variable, op: 'in' as const, value: [v] };
      newEdges.push({
        id: `${askKey}__${bKey}`, source: askKey, target: bKey,
        label: edgeLabel(`${variable} = ${v}`, guard),
        data: { condition_description: `${variable} = ${v}`, guard },
        markerEnd: { type: MarkerType.ArrowClosed },
      });
    });

    setNodes((nds) => [...nds, ...newNodes]);
    setEdges((eds) => [...eds, ...newEdges]);
    setSelectedNodeKey(askKey);
    setSelectedEdgeId(null);
    setDirty(true);
  }

  /**
   * שליחת API בסיום שיחה: שלב function (auto_tool) שנשתל לפני שלב הסיום —
   * כל הקשתות שנכנסו לסיום מנותבות דרכו, והכלי רץ אוטומטית לפני הניתוק.
   */
  function addEndApi(toolId: number, endKey: string) {
    const endNode = nodes.find((n) => n.data.stage.key === endKey);
    if (!endNode) return;
    snapshot();
    const fnKey = slugify('send_api', existingKeys);
    const fnStage: VoiceFlowStage = {
      key: fnKey, name: 'שליחת API בסיום', type: 'function',
      prompt: '', allowed_tools: [], collect_variables: [], auto_tool_id: toolId,
      canvas: { x: endNode.position.x - 60, y: endNode.position.y + 170 },
    };
    setNodes((nds) => [...nds, stageToNode(fnStage, 0)]);
    setEdges((eds) => [
      ...eds
        .map((e) => (e.target === endKey
          ? { ...e, id: `${e.source}__${fnKey}`, target: fnKey }
          : e)),
      {
        id: `${fnKey}__${endKey}`, source: fnKey, target: endKey,
        data: { condition_description: 'לאחר שליחת ה-API' }, markerEnd: { type: MarkerType.ArrowClosed },
      },
    ]);
    setSelectedNodeKey(fnKey);
    setSelectedEdgeId(null);
    setDirty(true);
  }

  // שכפול שלב: עותק עם key ייחודי ו-offset במיקום.
  function duplicateStage(key: string) {
    const src = nodes.find((n) => n.data.stage.key === key);
    if (!src) return;
    snapshot();
    const newKey = slugify(`${src.data.stage.key}_copy`, existingKeys);
    const copy: VoiceFlowStage = {
      ...src.data.stage,
      key: newKey,
      name: `${src.data.stage.name} (עותק)`,
      // עותק לעולם לא start כפול.
      type: src.data.stage.type === 'start' ? 'normal' : src.data.stage.type,
      collect_variables: (src.data.stage.collect_variables ?? []).map((v) => ({ ...v })),
      allowed_tools: [...(src.data.stage.allowed_tools ?? [])],
      canvas: { x: (src.position.x ?? 80) + 60, y: (src.position.y ?? 80) + 60 },
    };
    setNodes((nds) => [...nds, { ...stageToNode(copy, nds.length), position: copy.canvas! }]);
    setSelectedNodeKey(newKey);
  }

  // ── Update a stage from the side panel ──
  const patchStage = useCallback((key: string, patch: Partial<VoiceFlowStage>) => {
    snapshot();
    setNodes((nds) => nds.map((n) => {
      if (n.data.stage.key !== key) return n;
      const nextStage = { ...n.data.stage, ...patch };
      return { ...n, id: nextStage.key, data: { ...n.data, stage: nextStage } };
    }));
    if (patch.key && patch.key !== key) {
      setEdges((eds) => eds.map((e) => ({
        ...e,
        source: e.source === key ? patch.key! : e.source,
        target: e.target === key ? patch.key! : e.target,
        id: `${e.source === key ? patch.key : e.source}__${e.target === key ? patch.key : e.target}`,
      })));
      setSelectedNodeKey(patch.key);
    }
  }, [snapshot]);

  // עדכון קשת: תנאי ו/או guard. undefined = "אל תיגע"; guard=null = נקה תנאי-משתנה.
  const patchEdge = useCallback((id: string, patch: { condition?: string; guard?: VoiceTransitionGuard | null }) => {
    snapshot();
    setEdges((eds) => eds.map((e) => {
      if (e.id !== id) return e;
      const condition = patch.condition !== undefined ? patch.condition : ((e.data?.condition_description as string) ?? '');
      const guard = patch.guard !== undefined ? (patch.guard ?? undefined) : (e.data?.guard as VoiceTransitionGuard | undefined);
      return { ...e, label: edgeLabel(condition, guard), data: { ...e.data, condition_description: condition, guard } };
    }));
  }, [snapshot]);

  const deleteSelected = useCallback(() => {
    if (selectedNodeKey) {
      snapshot();
      setNodes((nds) => nds.filter((n) => n.data.stage.key !== selectedNodeKey));
      setEdges((eds) => eds.filter((e) => e.source !== selectedNodeKey && e.target !== selectedNodeKey));
      setSelectedNodeKey(null);
    } else if (selectedEdgeId) {
      snapshot();
      setEdges((eds) => eds.filter((e) => e.id !== selectedEdgeId));
      setSelectedEdgeId(null);
    }
  }, [selectedNodeKey, selectedEdgeId, snapshot]);

  // סידור אוטומטי עם dagre (LTR שמאל→ימין).
  function autoLayout() {
    snapshot();
    const g = new Dagre.graphlib.Graph();
    g.setDefaultEdgeLabel(() => ({}));
    g.setGraph({ rankdir: 'LR', nodesep: 60, ranksep: 120 });
    nodes.forEach((n) => g.setNode(n.id, { width: 200, height: 90 }));
    edges.forEach((e) => g.setEdge(e.source, e.target));
    Dagre.layout(g);
    setNodes((nds) => nds.map((n) => {
      const pos = g.node(n.id);
      return pos ? { ...n, position: { x: pos.x - 100, y: pos.y - 45 } } : n;
    }));
  }

  // ── Serialize for save/validate/simulate ──
  const buildBody = useCallback((): { stages: VoiceFlowStage[]; transitions: VoiceFlowTransition[] } => {
    const stages = nodes.map((n) => ({
      ...n.data.stage,
      canvas: { x: Math.round(n.position.x), y: Math.round(n.position.y) },
    }));
    const transitions = edges.map((e) => ({
      from: e.source, to: e.target,
      condition_description: (e.data?.condition_description as string) ?? '',
      // guard מועבר as-is (ה-GET כבר מחזיר אותו).
      guard: (e.data?.guard as VoiceTransitionGuard | undefined) ?? null,
    }));
    return { stages, transitions };
  }, [nodes, edges]);

  async function save() {
    setSaving(true);
    setBanner(null);
    try {
      const { warnings } = await superApi.saveVoiceFlowGraph(flowId, buildBody());
      toast('הזרימה נשמרה', 'ok');
      setDirty(false);
      if (warnings.length) setBanner({ errors: [], warnings });
    } catch (e: unknown) {
      const resp = (e as { response?: { data?: { errors?: string[]; warnings?: string[]; message?: string } } })?.response?.data;
      if (resp?.errors?.length || resp?.warnings?.length) {
        setBanner({ errors: resp.errors ?? [], warnings: resp.warnings ?? [] });
        toast('השמירה נחסמה — יש שגיאות לתקן', 'danger');
      } else {
        toast(resp?.message ?? 'שגיאה בשמירה', 'danger');
      }
    } finally {
      setSaving(false);
    }
  }

  async function validate() {
    setValidating(true);
    try {
      const res = await superApi.validateVoiceFlow(flowId, buildBody());
      setBanner({ errors: res.errors, warnings: res.warnings });
      if (!res.errors.length && !res.warnings.length) toast('הזרימה תקינה', 'ok');
    } catch {
      toast('שגיאה בבדיקת התקינות', 'danger');
    } finally {
      setValidating(false);
    }
  }

  function handleBack() {
    if (dirty) { setConfirmBack(true); return; }
    onBack();
  }

  // beforeunload — אזהרה על יציאה עם שינויים לא שמורים.
  useEffect(() => {
    if (!dirty) return;
    const h = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [dirty]);

  // מקלדת: Delete/Backspace מחיקה, Ctrl+S שמירה, Ctrl+Z / Ctrl+Shift+Z undo/redo.
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const typing = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
      const mod = e.ctrlKey || e.metaKey;
      if (mod && (e.key === 's' || e.key === 'S')) { e.preventDefault(); void save(); return; }
      if (mod && (e.key === 'z' || e.key === 'Z')) { e.preventDefault(); if (e.shiftKey) redo(); else undo(); return; }
      if (mod && (e.key === 'y' || e.key === 'Y')) { e.preventDefault(); redo(); return; }
      if (typing) return;
      if ((e.key === 'Delete' || e.key === 'Backspace') && (selectedNodeKey || selectedEdgeId)) {
        e.preventDefault();
        deleteSelected();
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNodeKey, selectedEdgeId, deleteSelected]);

  const selectedNode = nodes.find((n) => n.data.stage.key === selectedNodeKey) ?? null;
  const selectedEdge = edges.find((e) => e.id === selectedEdgeId) ?? null;

  const variableIndex = useMemo(() => {
    const out: { name: string; type: string; stageName: string; stageKey: string }[] = [];
    for (const n of nodes) {
      for (const v of n.data.stage.collect_variables ?? []) {
        if (v.name?.trim()) out.push({ name: v.name, type: v.type, stageName: n.data.stage.name || n.data.stage.key, stageKey: n.data.stage.key });
      }
    }
    return out;
  }, [nodes]);

  // משתני response_mappings מכלי הספרייה (לעוזר המשתנים / סריקת unknown).
  const mappingVars = useMemo(
    () => libraryTools.flatMap((t) => (t.response_mappings ?? []).map((m) => m.variable).filter(Boolean)),
    [libraryTools],
  );
  const knownVariables = useMemo(
    () => Array.from(new Set([...SYSTEM_VARIABLES, ...variableIndex.map((v) => v.name), ...mappingVars])),
    [variableIndex, mappingVars],
  );

  // ולידציה בצד-לקוח על כל שינוי — מריצה מחדש ומעדכנת את הטבעות בצמתים.
  const { warnings: liveWarnings, nodeIssue } = useMemo(() => computeIssues(nodes, edges, knownVariables), [nodes, edges, knownVariables]);

  // הזרקת issue + סטטיסטיקה לצמתים לצורך תצוגה (בלי לשנות את מקור-האמת stage).
  const decoratedNodes = useMemo(() => nodes.map((n) => {
    const st = stats?.stages.find((s) => s.stage_key === n.data.stage.key);
    return {
      ...n,
      data: {
        ...n.data,
        issue: nodeIssue[n.data.stage.key] ?? null,
        stat: statsDays != null && st ? { entered: st.entered, dropped: st.dropped_here } : null,
      },
    };
  }), [nodes, nodeIssue, stats, statsDays]);

  const functionStages = nodes.filter((n) => n.data.stage.type !== 'start');

  // טעינת סטטיסטיקה כשמפעילים את התצוגה / משנים ימים.
  useEffect(() => {
    if (statsDays == null) { setStats(null); return; }
    let active = true;
    void superApi.voiceFlowStats(flowId, statsDays)
      .then((s) => { if (active) setStats(s); })
      .catch(() => { if (active) toast('שגיאה בטעינת הנתונים', 'danger'); });
    return () => { active = false; };
  }, [statsDays, flowId]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px var(--gap)', borderBottom: '1px solid var(--line)', flexWrap: 'wrap' }}>
        <button type="button" className="btn ghost" onClick={handleBack}>← חזרה לרשימה</button>
        <strong style={{ fontSize: 'var(--t-md)' }}>{meta?.name ?? 'טוען…'}</strong>
        {dirty && <span style={{ fontSize: 'var(--t-xs)', color: 'var(--warn, #f59e0b)' }}>● שינויים לא שמורים</span>}
        <div style={{ flex: 1 }} />

        <button type="button" className="btn" onClick={undo} disabled={histLen === 0} title="בטל (Ctrl+Z)">↶</button>
        <button type="button" className="btn" onClick={redo} disabled={futLen === 0} title="בצע שוב (Ctrl+Shift+Z)">↷</button>
        <button type="button" className="btn" onClick={addStage}>+ הוסף שלב</button>
        <button type="button" className="btn" onClick={() => setIfOpen(true)} title="שלב שאלה + ענף לכל תשובה, עם תנאי על כל קשת">⎇ פיצול IF</button>
        <button type="button" className="btn" onClick={() => setEndApiOpen(true)} title="הרצת כלי API אוטומטית לפני סיום השיחה">API בסיום</button>
        <button type="button" className="btn" onClick={autoLayout}>סדר אוטומטית</button>
        <button type="button" className="btn" onClick={() => setSimOpen(true)}>סימולציה</button>
        <button type="button" className="btn" onClick={() => setVarsOpen((v) => !v)}>משתנים ({variableIndex.length})</button>
        {liveWarnings.length > 0 && (
          <button type="button" className="btn" style={{ color: 'var(--warn, #f59e0b)' }} onClick={() => setIssuesOpen((v) => !v)}>
            {liveWarnings.length} אזהרות
          </button>
        )}
        {/* Analytics toggle */}
        {statsDays == null ? (
          <button type="button" className="btn" onClick={() => setStatsDays(30)}>נתונים (30 יום)</button>
        ) : (
          <>
            <FormSelect value={String(statsDays)} onChange={(e) => setStatsDays(Number(e.target.value))} style={{ width: 90, height: 34 }}>
              <option value="7">7 יום</option>
              <option value="30">30 יום</option>
              <option value="90">90 יום</option>
            </FormSelect>
            <button type="button" className="btn ghost" onClick={() => setStatsDays(null)}>הסתר נתונים</button>
          </>
        )}
        <button type="button" className="btn" onClick={validate} disabled={validating}>
          {validating ? 'בודק…' : 'בדוק תקינות'}
        </button>
        <button type="button" className="btn primary" onClick={save} disabled={saving}>
          {saving ? 'שומר…' : 'שמור'}
        </button>
      </div>

      {statsDays != null && stats && (
        <div style={{ padding: '6px var(--gap)', fontSize: 'var(--t-sm)', color: 'var(--muted)', borderBottom: '1px solid var(--line)' }}>
          {stats.total_calls} שיחות · {stats.completed} הושלמו · {stats.days} יום
        </div>
      )}

      {issuesOpen && liveWarnings.length > 0 && (
        <div style={{ padding: '8px var(--gap)', display: 'grid', gap: 4, borderBottom: '1px solid var(--line)' }}>
          {liveWarnings.map((w, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center', color: 'var(--warn, #f59e0b)', fontSize: 'var(--t-sm)' }}>
              <Icon name="warn" size={14} color="var(--warn, #f59e0b)" /> {w}
            </div>
          ))}
        </div>
      )}

      {banner && (banner.errors.length > 0 || banner.warnings.length > 0) && (
        <div style={{ padding: '8px var(--gap)', display: 'grid', gap: 4 }}>
          {banner.errors.map((er, i) => (
            <div key={`e${i}`} style={{ display: 'flex', gap: 6, alignItems: 'center', color: 'var(--danger)', fontSize: 'var(--t-sm)' }}>
              <Icon name="danger" size={14} color="var(--danger)" /> {er}
            </div>
          ))}
          {banner.warnings.map((w, i) => (
            <div key={`w${i}`} style={{ display: 'flex', gap: 6, alignItems: 'center', color: 'var(--warn, #f59e0b)', fontSize: 'var(--t-sm)' }}>
              <Icon name="warn" size={14} color="var(--warn, #f59e0b)" /> {w}
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {/* Canvas */}
        <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
          {loading ? (
            <div style={{ padding: 24, color: 'var(--muted)' }}>טוען…</div>
          ) : (
            <div dir="ltr" style={{ position: 'absolute', inset: 0 }}>
              {/* עיצוב הקנבס: קשתות מקווקוות זורמות + ריחוף עדין על צמתים. */}
              <style>{`
                @keyframes vf-flow { from { stroke-dashoffset: 24; } to { stroke-dashoffset: 0; } }
                @keyframes vf-pulse { 0%,100% { opacity: 1; } 50% { opacity: .35; } }
                .react-flow__edge-path {
                  stroke: var(--accent);
                  stroke-width: 2.5;
                  stroke-dasharray: 6 6;
                  animation: vf-flow 1s linear infinite;
                }
                .react-flow__edge.selected .react-flow__edge-path { stroke-width: 3.5; }
                .vf-node { transition: box-shadow .25s, transform .25s, border-color .25s; }
                .vf-node:hover { transform: translateY(-2px); box-shadow: var(--shadow-3); }
              `}</style>
              <ReactFlow
                nodes={decoratedNodes}
                edges={edges}
                nodeTypes={NODE_TYPES}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                onNodeClick={(_, n) => { setSelectedNodeKey(n.data.stage.key); setSelectedEdgeId(null); }}
                onEdgeClick={(_, e) => { setSelectedEdgeId(e.id); setSelectedNodeKey(null); }}
                onPaneClick={() => { setSelectedNodeKey(null); setSelectedEdgeId(null); }}
                fitView
                proOptions={{ hideAttribution: true }}
              >
                <Background gap={24} size={1.5} color="color-mix(in oklch, var(--muted) 45%, transparent)" />
                <Controls />
                <MiniMap pannable zoomable />
              </ReactFlow>
            </div>
          )}
        </div>

        {/* Variables panel (collapsible) */}
        {varsOpen && (
          <div style={{ width: 280, borderInlineStart: '1px solid var(--line)', overflow: 'auto', padding: 'var(--gap)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <strong style={{ fontSize: 'var(--t-sm)' }}>משתני הזרימה</strong>
              <button type="button" className="btn ghost" style={{ height: 28 }} onClick={() => setVarsOpen(false)}>סגור</button>
            </div>
            {variableIndex.length === 0
              ? <p style={{ color: 'var(--muted)', fontSize: 'var(--t-xs)' }}>אין משתנים נאספים עדיין. הוסף בשלב דרך "משתנים לאיסוף".</p>
              : (
                <div className="stack" style={{ gap: 8 }}>
                  {variableIndex.map((v, i) => (
                    <div key={i} style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-2)', padding: 8 }}>
                      <div dir="ltr" style={{ fontSize: 'var(--t-sm)', fontWeight: 600 }}>{v.name} <span style={{ color: 'var(--muted)', fontWeight: 400 }}>:{v.type}</span></div>
                      <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>מ: {v.stageName}</div>
                    </div>
                  ))}
                </div>
              )}
          </div>
        )}

        {/* Node side panel */}
        {selectedNode && (
          <StagePanel
            key={selectedNode.data.stage.key}
            stage={selectedNode.data.stage}
            allKeys={[...existingKeys]}
            toolNames={toolNameOptions}
            libraryTools={libraryTools}
            tenants={builderTenants}
            onReloadTools={() => void reloadLibraryTools()}
            knownVariables={knownVariables}
            dirty={dirty}
            onPatch={(p) => patchStage(selectedNode.data.stage.key, p)}
            onDuplicate={() => duplicateStage(selectedNode.data.stage.key)}
            onPreview={() => setPreviewKey(selectedNode.data.stage.key)}
            onDelete={() => { deleteSelected(); }}
            onClose={() => setSelectedNodeKey(null)}
          />
        )}

        {/* Edge side panel */}
        {selectedEdge && (
          <EdgePanel
            edge={selectedEdge}
            knownVariables={knownVariables}
            onSaveCondition={(cond) => patchEdge(selectedEdge.id, { condition: cond })}
            onSaveGuard={(guard) => patchEdge(selectedEdge.id, { guard })}
            onDelete={() => { deleteSelected(); }}
            onClose={() => setSelectedEdgeId(null)}
          />
        )}

        {/* Simulation drawer */}
      {ifOpen && (
        <IfSplitModal
          onClose={() => setIfOpen(false)}
          onCreate={(q, v, vals) => { addIfSplit(q, v, vals); setIfOpen(false); }}
        />
      )}
      {endApiOpen && (
        <EndApiModal
          tools={libraryTools}
          endStages={nodes.filter((n) => n.data.stage.type === 'end_call').map((n) => ({ key: n.data.stage.key, name: n.data.stage.name }))}
          onClose={() => setEndApiOpen(false)}
          onCreate={(toolId, endKey) => { addEndApi(toolId, endKey); setEndApiOpen(false); }}
        />
      )}
        {simOpen && (
          <SimulationPanel
            flowId={flowId}
            dirty={dirty}
            agents={flowAgents}
            onClose={() => setSimOpen(false)}
          />
        )}
      </div>

      {previewKey && (
        <StagePreviewModal flowId={flowId} stageKey={previewKey} onClose={() => setPreviewKey(null)} />
      )}

      <ConfirmDialog
        isOpen={confirmBack}
        onClose={() => setConfirmBack(false)}
        onConfirm={() => { setConfirmBack(false); onBack(); }}
        title="שינויים לא שמורים"
        message="יש שינויים שלא נשמרו. לצאת ולאבד אותם?"
        confirmLabel="צא בלי לשמור"
        tone="warn"
      />
    </div>
  );
}

function stageToNode(stage: VoiceFlowStage, i: number): FlowNode {
  return {
    id: stage.key,
    type: 'stage',
    position: { x: stage.canvas?.x ?? 80 + (i % 4) * 240, y: stage.canvas?.y ?? 80 + Math.floor(i / 4) * 160 },
    data: { stage },
  };
}

// תווית הקשת = התנאי המילולי + צ'יפ guard (אם קיים) כדי שמעברים מותנים בולטים.
function edgeLabel(condition?: string | null, guard?: VoiceTransitionGuard | null): string | undefined {
  const parts: string[] = [];
  if (condition?.trim()) parts.push(condition.trim());
  if (guard?.variable) parts.push(`⎇ ${guardSummary(guard)}`);
  return parts.length ? parts.join('  ') : undefined;
}

function transitionToEdge(t: VoiceFlowTransition): Edge {
  return {
    id: `${t.from}__${t.to}`,
    source: t.from, target: t.to,
    label: edgeLabel(t.condition_description, t.guard),
    data: { condition_description: t.condition_description ?? '', guard: t.guard ?? undefined },
    markerEnd: { type: MarkerType.ArrowClosed },
  };
}

// ── פיצול IF ──────────────────────────────────────────────────────────────────
function IfSplitModal({ onClose, onCreate }: {
  onClose: () => void;
  onCreate: (question: string, variable: string, values: string[]) => void;
}) {
  const [question, setQuestion] = useState('תרצה להזמין בשר או חלב?');
  const [variable, setVariable] = useState('order_type');
  const [valuesText, setValuesText] = useState('בשר\nחלב');

  function create() {
    const values = valuesText.split('\n').map((v) => v.trim()).filter(Boolean);
    if (!question.trim() || !variable.trim() || values.length < 2) {
      toast('יש למלא שאלה, שם משתנה ולפחות שתי תשובות', 'warn');
      return;
    }
    onCreate(question.trim(), variable.trim().toLowerCase().replace(/[^a-z0-9_]+/g, '_'), values);
  }

  return (
    <Modal isOpen onClose={onClose} title="פיצול IF — שאלה עם ענפים"
      footer={<><button type="button" className="btn" onClick={onClose}>ביטול</button>
        <button type="button" className="btn primary" onClick={create}>צור פיצול</button></>}>
      <div className="stack">
        <p style={{ margin: 0, fontSize: 'var(--t-sm)', color: 'var(--muted)' }}>
          נוצר שלב שבו הסוכן שואל את השאלה ושומר את התשובה במשתנה, וענף לכל תשובה —
          עם תנאי (guard) על כל קשת כך שהזרימה ממשיכה רק בענף המתאים.
        </p>
        <FormField label="השאלה ללקוח" required>
          <TextInput value={question} onChange={(e) => setQuestion(e.target.value)} />
        </FormField>
        <FormField label="שם המשתנה" hint="באנגלית — התשובה נשמרת בו וניתן להשתמש בו בהמשך (גם בכלי API)">
          <TextInput dir="ltr" value={variable} onChange={(e) => setVariable(e.target.value)} />
        </FormField>
        <FormField label="התשובות האפשריות (שורה לכל ענף)" required>
          <TextArea rows={4} value={valuesText} onChange={(e) => setValuesText(e.target.value)} />
        </FormField>
      </div>
    </Modal>
  );
}

// ── שליחת API בסיום שיחה ──────────────────────────────────────────────────────
function EndApiModal({ tools, endStages, onClose, onCreate }: {
  tools: VoiceTool[];
  endStages: { key: string; name: string }[];
  onClose: () => void;
  onCreate: (toolId: number, endKey: string) => void;
}) {
  const [toolId, setToolId] = useState<string>(tools[0] ? String(tools[0].id) : '');
  const [endKey, setEndKey] = useState<string>(endStages[0]?.key ?? '');

  return (
    <Modal isOpen onClose={onClose} title="שליחת API בסיום השיחה"
      footer={<><button type="button" className="btn" onClick={onClose}>ביטול</button>
        <button type="button" className="btn primary" disabled={!toolId || !endKey}
          onClick={() => onCreate(Number(toolId), endKey)}>הוסף</button></>}>
      <div className="stack">
        <p style={{ margin: 0, fontSize: 'var(--t-sm)', color: 'var(--muted)' }}>
          נשתל שלב "פונקציה" שמריץ את הכלי אוטומטית לפני שלב הסיום — כל המסלולים
          שמגיעים לסיום עוברים דרכו. משתני השיחה (כמו order_type) זמינים לכלי.
        </p>
        {tools.length === 0 ? (
          <p style={{ margin: 0, color: 'var(--warn, #f59e0b)', fontSize: 'var(--t-sm)' }}>
            אין כלי API — צור אחד בלשונית "כלי API" ואז חזור לכאן.
          </p>
        ) : (
          <FormField label="כלי ה-API להרצה" required>
            <FormSelect value={toolId} onChange={(e) => setToolId(e.target.value)}>
              {tools.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </FormSelect>
          </FormField>
        )}
        {endStages.length === 0 ? (
          <p style={{ margin: 0, color: 'var(--warn, #f59e0b)', fontSize: 'var(--t-sm)' }}>
            אין שלב "סיום שיחה" בזרימה — הוסף אחד קודם.
          </p>
        ) : (
          <FormField label="לפני איזה שלב סיום" required>
            <FormSelect value={endKey} onChange={(e) => setEndKey(e.target.value)}>
              {endStages.map((st) => <option key={st.key} value={st.key}>{st.name}</option>)}
            </FormSelect>
          </FormField>
        )}
      </div>
    </Modal>
  );
}

// ── Stage side panel ──────────────────────────────────────────────────────────
const CONDITION_SUGGESTIONS = ['הלקוח אישר את הפרטים', 'הלקוח רוצה נציג', 'הפרטים נאספו', 'הלקוח סירב'];

function StagePanel({
  stage, allKeys, toolNames, libraryTools, tenants, onReloadTools, knownVariables, dirty, onPatch, onDuplicate, onPreview, onDelete, onClose,
}: {
  stage: VoiceFlowStage;
  tenants: TenantLite[];
  onReloadTools: () => void;
  allKeys: string[];
  toolNames: string[];
  libraryTools: VoiceTool[];
  knownVariables: string[];
  dirty: boolean;
  onPatch: (patch: Partial<VoiceFlowStage>) => void;
  onDuplicate: () => void;
  onPreview: () => void;
  onDelete: () => void;
  onClose: () => void;
}) {
  const [keyDraft, setKeyDraft] = useState(stage.key);
  const [createToolOpen, setCreateToolOpen] = useState(false);
  const keyValid = /^[a-z][a-z0-9_]*$/.test(keyDraft.trim());
  const keyChanged = keyDraft.trim() !== stage.key;
  const promptRef = useRef<HTMLTextAreaElement | null>(null);

  const allowed = stage.allowed_tools ?? [];
  function toggleAllowed(name: string) {
    onPatch({ allowed_tools: allowed.includes(name) ? allowed.filter((n) => n !== name) : [...allowed, name] });
  }

  const vars = stage.collect_variables ?? [];
  function patchVar(i: number, p: Partial<VoiceStageVariable>) {
    onPatch({ collect_variables: vars.map((v, j) => (j === i ? { ...v, ...p } : v)) });
  }

  // הוספת {{name}} במיקום הסמן בתיבת ההנחיות.
  function insertVariable(name: string) {
    const el = promptRef.current;
    const token = `{{${name}}}`;
    const cur = stage.prompt ?? '';
    if (!el) { onPatch({ prompt: cur + token }); return; }
    const start = el.selectionStart ?? cur.length;
    const end = el.selectionEnd ?? cur.length;
    const next = cur.slice(0, start) + token + cur.slice(end);
    onPatch({ prompt: next });
    requestAnimationFrame(() => { el.focus(); const p = start + token.length; el.setSelectionRange(p, p); });
  }

  // סריקת {{vars}} בהנחיות מול הסט הידוע.
  const unknownVars = useMemo(() => {
    const found = [...(stage.prompt ?? '').matchAll(/\{\{\s*([a-zA-Z0-9_.]+)\s*(?:\|[^}]*)?\}\}/g)].map((m) => m[1].split('.')[0]);
    return Array.from(new Set(found.filter((v) => !knownVariables.includes(v))));
  }, [stage.prompt, knownVariables]);

  return (
    <div style={{ width: 340, borderInlineStart: '1px solid var(--line)', overflow: 'auto', padding: 'var(--gap)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <strong style={{ fontSize: 'var(--t-md)' }}>עריכת שלב</strong>
        <button type="button" className="btn ghost" style={{ height: 28 }} onClick={onClose}>סגור</button>
      </div>

      <div className="stack">
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            type="button" className="btn" style={{ height: 30, flex: 1 }}
            onClick={onPreview} disabled={dirty}
            title={dirty ? 'שמור קודם — התצוגה מגיעה מהשרת' : undefined}
          >
            מה המודל רואה
          </button>
          <button type="button" className="btn" style={{ height: 30, flex: 1 }} onClick={onDuplicate}>שכפל שלב</button>
        </div>

        <FormField label="שם">
          <TextInput value={stage.name} onChange={(e) => onPatch({ name: e.target.value })} />
        </FormField>

        <FormField
          label="מפתח (key)"
          hint="שינוי המפתח מחווט מחדש את כל המעברים לשלב הזה"
          error={keyDraft && !keyValid ? 'snake_case בלבד (אות קטנה בהתחלה)' : undefined}
        >
          <div style={{ display: 'flex', gap: 6 }}>
            <TextInput value={keyDraft} dir="ltr" onChange={(e) => setKeyDraft(e.target.value)} />
            <button
              type="button" className="btn"
              disabled={!keyChanged || !keyValid || allKeys.includes(keyDraft.trim())}
              onClick={() => onPatch({ key: keyDraft.trim() })}
            >
              עדכן
            </button>
          </div>
          {keyChanged && allKeys.includes(keyDraft.trim()) && (
            <span style={{ fontSize: 'var(--t-xs)', color: 'var(--danger)' }}>מפתח כבר קיים</span>
          )}
        </FormField>

        <FormField label="סוג">
          <FormSelect value={stage.type} onChange={(e) => onPatch({ type: e.target.value as VoiceStageType })}>
            {STAGE_TYPES.map((t) => <option key={t} value={t}>{STAGE_META[t].label}</option>)}
          </FormSelect>
        </FormField>

        <FormField label="הנחיות" hint="ההנחיות רק לשלב הזה">
          <div className="stack" style={{ gap: 4 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <VariableInserter variables={knownVariables} onPick={insertVariable} />
            </div>
            <textarea
              ref={promptRef}
              rows={4}
              value={stage.prompt ?? ''}
              onChange={(e) => onPatch({ prompt: e.target.value })}
              style={{
                width: '100%', padding: '10px 12px', borderRadius: 'var(--r-2)',
                border: '1px solid var(--line)', background: 'var(--surface)', color: 'var(--text)',
                fontSize: 'var(--t-md)', fontFamily: 'inherit', outline: 'none', resize: 'vertical',
              }}
            />
            {unknownVars.map((v) => (
              <span key={v} style={{ fontSize: 'var(--t-xs)', color: 'var(--warn, #f59e0b)' }}>
                משתנה לא מוכר: {`{{${v}}}`} — ודא שמשהו מייצר אותו
              </span>
            ))}
          </div>
        </FormField>

        {stage.type === 'function' && (
          <FormField label="כלי אוטומטי (auto_tool)" hint="הכלי שהשלב מריץ אוטומטית">
            <FormSelect
              value={stage.auto_tool_id != null ? String(stage.auto_tool_id) : ''}
              onChange={(e) => onPatch({ auto_tool_id: e.target.value ? Number(e.target.value) : null })}
            >
              <option value="">— בחר כלי —</option>
              {libraryTools.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </FormSelect>
          </FormField>
        )}

        {stage.type === 'transfer' && (
          <FormField label="יעד העברה" hint="שלוחה / תור / מספר או שלב יעד">
            <TextInput value={stage.transfer_target ?? ''} dir="ltr" onChange={(e) => onPatch({ transfer_target: e.target.value })} />
          </FormField>
        )}

        {/* allowed_tools: רשימה ריקה = כל כלי הסוכן מותרים (התנהגות ה-Orchestrator) */}
        <div style={{ borderTop: '1px solid var(--line)', paddingTop: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <strong style={{ fontSize: 'var(--t-sm)' }}>כלים מותרים בשלב</strong>
            <button type="button" className="btn ghost" style={{ fontSize: 'var(--t-xs)' }} onClick={() => setCreateToolOpen(true)}>+ כלי חדש</button>
          </div>
          <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', marginTop: 2 }}>
            כלי שמותר בשלב זמין לסוכן תוך כדי השיחה — למשל בדיקת מלאי: הלקוח שואל
            "יש פחית זירו?", הסוכן מריץ את הכלי ועונה לפי התשובה.
          </div>
        </div>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 'var(--t-sm)' }}>
          <input
            type="checkbox"
            checked={allowed.length === 0}
            onChange={() => {
              // סימון = חזרה ל"הכל מותר" (ריקון); ביטול = צמצום שמתחיל מכל הכלים.
              onPatch({ allowed_tools: allowed.length === 0 ? [...toolNames] : [] });
            }}
          />
          כל כלי הסוכן מותרים בשלב הזה
        </label>
        {allowed.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {toolNames.length === 0
              ? <span style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>אין כלים.</span>
              : toolNames.map((name) => (
                <label
                  key={name}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer',
                    padding: '4px 8px', borderRadius: 'var(--r-2)',
                    border: `1px solid ${allowed.includes(name) ? 'var(--accent)' : 'var(--line)'}`,
                    background: allowed.includes(name) ? 'var(--accent-soft, var(--surface-2))' : 'var(--surface)',
                    fontSize: 'var(--t-xs)',
                  }}
                >
                  <input type="checkbox" checked={allowed.includes(name)} onChange={() => toggleAllowed(name)} />
                  <span dir="ltr">{name}</span>
                </label>
              ))}
          </div>
        )}
        {createToolOpen && (
          <VoiceToolForm
            mode="create"
            tenants={tenants}
            onClose={() => setCreateToolOpen(false)}
            onSaved={(saved) => {
              setCreateToolOpen(false);
              onReloadTools();
              // אם השלב מצומצם — הכלי החדש מותר בו מיד.
              if (saved?.name && allowed.length > 0 && !allowed.includes(saved.name)) {
                onPatch({ allowed_tools: [...allowed, saved.name] });
              }
              toast('הכלי נוצר וזמין בזרימה', 'ok');
            }}
          />
        )}

        {/* collect_variables */}
        <div style={{ borderTop: '1px solid var(--line)', paddingTop: 8 }}>
          <strong style={{ fontSize: 'var(--t-sm)' }}>משתנים לאיסוף</strong>
        </div>
        {vars.map((v, i) => (
          <div key={i} style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-2)', padding: 8, display: 'grid', gap: 6 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px auto', gap: 6, alignItems: 'center' }}>
              <TextInput placeholder="שם" value={v.name} dir="ltr" onChange={(e) => patchVar(i, { name: e.target.value })} />
              <FormSelect value={v.type} onChange={(e) => patchVar(i, { type: e.target.value })}>
                <option value="string">string</option>
                <option value="number">number</option>
                <option value="integer">integer</option>
                <option value="boolean">boolean</option>
              </FormSelect>
              <button type="button" className="btn danger" style={{ height: 30 }}
                onClick={() => onPatch({ collect_variables: vars.filter((_, j) => j !== i) })}>הסר</button>
            </div>
            <TextInput placeholder="תיאור — מה לאסוף" value={v.description ?? ''} onChange={(e) => patchVar(i, { description: e.target.value })} />
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 'var(--t-sm)' }}>
              <input type="checkbox" checked={!!v.required} onChange={(e) => patchVar(i, { required: e.target.checked })} /> חובה
            </label>
          </div>
        ))}
        <div>
          <button type="button" className="btn"
            onClick={() => onPatch({ collect_variables: [...vars, { name: '', type: 'string', required: false }] })}>
            + משתנה
          </button>
        </div>

        <div style={{ borderTop: '1px solid var(--line)', paddingTop: 10 }}>
          <button type="button" className="btn danger" onClick={onDelete}>מחק שלב</button>
        </div>
      </div>
    </div>
  );
}

// עוזר משתנים — dropdown קטן שמכניס {{name}} בתיבת ההנחיות.
function VariableInserter({ variables, onPick }: { variables: string[]; onPick: (name: string) => void }) {
  const [val, setVal] = useState('');
  return (
    <FormSelect
      value={val}
      onChange={(e) => { const v = e.target.value; if (v) { onPick(v); setVal(''); } }}
      style={{ height: 30, fontSize: 'var(--t-xs)' }}
    >
      <option value="">+ הוסף משתנה</option>
      {variables.map((v) => <option key={v} value={v}>{v}</option>)}
    </FormSelect>
  );
}

// ── Edge side panel ───────────────────────────────────────────────────────────
function EdgePanel({ edge, knownVariables, onSaveCondition, onSaveGuard, onDelete, onClose }: {
  edge: Edge;
  knownVariables: string[];
  onSaveCondition: (condition: string) => void;
  onSaveGuard: (guard: VoiceTransitionGuard | null) => void;
  onDelete: () => void;
  onClose: () => void;
}) {
  const [cond, setCond] = useState<string>((edge.data?.condition_description as string) ?? '');
  const guard = (edge.data?.guard as VoiceTransitionGuard | undefined) ?? null;
  const [guardOpen, setGuardOpen] = useState(!!guard);

  // עריכת שדות ה-guard מעדכנת מיד למעלה (כדי שהתווית על הקנבס תתעדכן).
  function updateGuard(patch: Partial<VoiceTransitionGuard>) {
    const base: VoiceTransitionGuard = guard ?? { variable: '', op: 'eq' };
    onSaveGuard({ ...base, ...patch });
  }
  const valueless = guard ? VALUELESS_OPS.includes(guard.op) : false;
  const valueStr = guard ? (Array.isArray(guard.value) ? guard.value.join(', ') : (guard.value ?? '')) : '';

  return (
    <div style={{ width: 320, borderInlineStart: '1px solid var(--line)', overflow: 'auto', padding: 'var(--gap)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <strong style={{ fontSize: 'var(--t-md)' }}>עריכת מעבר</strong>
        <button type="button" className="btn ghost" style={{ height: 28 }} onClick={onClose}>סגור</button>
      </div>
      <div className="stack">
        <div dir="ltr" style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>{edge.source} → {edge.target}</div>
        <FormField label="תנאי המעבר" hint="מתי הסוכן עובר בין השלבים">
          <TextArea rows={3} value={cond} onChange={(e) => setCond(e.target.value)} onBlur={() => onSaveCondition(cond)} />
        </FormField>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {CONDITION_SUGGESTIONS.map((s) => (
            <button
              key={s} type="button"
              style={{
                fontSize: 'var(--t-xs)', padding: '3px 8px', borderRadius: 'var(--r-pill)',
                border: '1px solid var(--line)', background: 'var(--surface)', cursor: 'pointer',
              }}
              onClick={() => { setCond(s); onSaveCondition(s); }}
            >
              {s}
            </button>
          ))}
        </div>
        <button type="button" className="btn primary" onClick={() => onSaveCondition(cond)}>שמור תנאי</button>

        {/* תנאי-משתנה (guard) — מתקפל */}
        <div style={{ borderTop: '1px solid var(--line)', paddingTop: 10 }}>
          <button
            type="button"
            style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', padding: 0, font: 'inherit', color: 'inherit', cursor: 'pointer' }}
            onClick={() => setGuardOpen((v) => !v)}
          >
            <Icon name={guardOpen ? 'chevronD' : 'chevron'} size={14} />
            <strong style={{ fontSize: 'var(--t-sm)' }}>תנאי משתנה (אופציונלי)</strong>
            {guard && <StatusBadge tone="neutral" size="sm">{guardSummary(guard)}</StatusBadge>}
          </button>
        </div>
        {guardOpen && (
          <div className="stack" style={{ gap: 6 }}>
            <p style={{ color: 'var(--muted)', fontSize: 'var(--t-xs)', margin: 0 }}>
              המעבר יתאפשר רק אם התנאי על המשתנה מתקיים.
            </p>
            <FormField label="משתנה">
              <FormSelect
                value={guard?.variable ?? ''}
                onChange={(e) => updateGuard({ variable: e.target.value })}
              >
                <option value="">— בחר משתנה —</option>
                {knownVariables.map((v) => <option key={v} value={v}>{v}</option>)}
              </FormSelect>
            </FormField>
            <FormField label="אופרטור">
              <FormSelect
                value={guard?.op ?? 'eq'}
                onChange={(e) => {
                  const op = e.target.value as VoiceGuardOp;
                  // מעבר לאופרטור ללא-ערך מנקה את הערך.
                  updateGuard(VALUELESS_OPS.includes(op) ? { op, value: undefined } : { op });
                }}
              >
                {GUARD_OPS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </FormSelect>
            </FormField>
            {!valueless && (
              <FormField label="ערך" hint={guard?.op === 'in' ? 'רשימה מופרדת בפסיקים' : undefined}>
                <TextInput
                  value={valueStr}
                  dir="ltr"
                  onChange={(e) => {
                    const raw = e.target.value;
                    const val: string | string[] = guard?.op === 'in'
                      ? raw.split(',').map((s) => s.trim()).filter(Boolean)
                      : raw;
                    updateGuard({ value: val });
                  }}
                />
              </FormField>
            )}
            <button type="button" className="btn" onClick={() => { onSaveGuard(null); setGuardOpen(false); }}>נקה תנאי</button>
          </div>
        )}

        <div style={{ borderTop: '1px solid var(--line)', paddingTop: 10 }}>
          <button type="button" className="btn danger" onClick={onDelete}>מחק מעבר</button>
        </div>
      </div>
    </div>
  );
}

// ── Stage preview modal (מה המודל רואה) ──────────────────────────────────────
function StagePreviewModal({ flowId, stageKey, onClose }: { flowId: number; stageKey: string; onClose: () => void }) {
  const [data, setData] = useState<VoiceFlowStagePreview | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    void superApi.voiceFlowStagePreview(flowId, stageKey)
      .then(setData)
      .catch(() => toast('שגיאה בטעינת התצוגה', 'danger'))
      .finally(() => setLoading(false));
  }, [flowId, stageKey]);

  return (
    <Modal isOpen onClose={onClose} size="lg" title={`מה המודל רואה — ${stageKey}`}
      footer={<button type="button" className="btn" onClick={onClose}>סגור</button>}>
      {loading ? (
        <div style={{ color: 'var(--muted)' }}>טוען…</div>
      ) : data ? (
        <div className="stack">
          <pre dir="rtl" style={{
            margin: 0, padding: 12, borderRadius: 'var(--r-2)', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            background: 'var(--surface-2)', border: '1px solid var(--line)', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-sm)',
            maxHeight: 420, overflow: 'auto',
          }}>
            {data.block}
          </pre>
          {data.internal_tools.length > 0 && (
            <div>
              <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', marginBottom: 4 }}>כלים פנימיים:</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {data.internal_tools.map((t) => <StatusBadge key={t} tone="neutral" size="sm">{t}</StatusBadge>)}
              </div>
            </div>
          )}
          {data.note && <p style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', margin: 0 }}>{data.note}</p>}
        </div>
      ) : null}
    </Modal>
  );
}

// ── Simulation panel (text chat) ─────────────────────────────────────────────
type ChatItem =
  | { kind: 'msg'; role: 'user' | 'model'; text: string }
  | { kind: 'event'; event: VoiceSimEvent };

function eventLabel(e: VoiceSimEvent): string {
  switch (e.type) {
    case 'move_to_stage': return `⟶ עבר לשלב ${(e as { stage_key?: string }).stage_key ?? '?'}${(e as { reason?: string }).reason ? ` (${(e as { reason?: string }).reason})` : ''}`;
    case 'move_rejected': return `⛔ מעבר נדחה${(e as { reason?: string }).reason ? ` (${(e as { reason?: string }).reason})` : ''}`;
    case 'save_variable': return `💾 ${(e as { name?: string }).name ?? '?'}=${String((e as { value?: unknown }).value ?? '')}`;
    case 'tool_stub': return `🛠️ ${(e as { tool?: string }).tool ?? '?'} (מדומה)`;
    default: return `• ${e.type}`;
  }
}

function SimulationPanel({ flowId, dirty, agents, onClose }: {
  flowId: number;
  dirty: boolean;
  agents: VoiceAgentRow[];
  onClose: () => void;
}) {
  const [items, setItems] = useState<ChatItem[]>([]);
  const [history, setHistory] = useState<Array<{ role: 'user' | 'model'; text: string }>>([]);
  const [state, setState] = useState<{ stage_key: string | null; variables: Record<string, unknown> }>({ stage_key: null, variables: {} });
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [agentId, setAgentId] = useState('');
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [items]);

  async function send() {
    const message = input.trim();
    if (!message || sending) return;
    setInput('');
    setItems((p) => [...p, { kind: 'msg', role: 'user', text: message }]);
    setSending(true);
    try {
      const turn: VoiceSimTurn = await superApi.simulateVoiceFlow(flowId, {
        message,
        agent_id: agentId ? Number(agentId) : undefined,
        state: { stage_key: state.stage_key ?? undefined, variables: state.variables },
        history,
      });
      const evItems: ChatItem[] = (turn.events ?? []).map((event) => ({ kind: 'event', event }));
      setItems((p) => [...p, ...evItems, { kind: 'msg', role: 'model', text: turn.reply }]);
      setState(turn.state);
      setHistory((h) => [...h, { role: 'user', text: message }, { role: 'model', text: turn.reply }]);
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'שגיאה בסימולציה';
      toast(msg, 'danger');
    } finally {
      setSending(false);
    }
  }

  function reset() {
    setItems([]); setHistory([]); setState({ stage_key: null, variables: {} });
  }

  const varEntries = Object.entries(state.variables ?? {}).filter(([, v]) => v != null && v !== '');

  return (
    <div style={{ width: 360, borderInlineStart: '1px solid var(--line)', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 'var(--gap)', borderBottom: '1px solid var(--line)' }}>
        <strong style={{ fontSize: 'var(--t-md)' }}>סימולציה</strong>
        <div style={{ display: 'flex', gap: 6 }}>
          <button type="button" className="btn ghost" style={{ height: 28 }} onClick={reset}>אתחל</button>
          <button type="button" className="btn ghost" style={{ height: 28 }} onClick={onClose}>סגור</button>
        </div>
      </div>

      {dirty && (
        <div style={{ padding: '8px var(--gap)', fontSize: 'var(--t-xs)', color: 'var(--warn, #f59e0b)', borderBottom: '1px solid var(--line)' }}>
          שמור את הזרימה כדי לסמלץ את הגרסה הנוכחית
        </div>
      )}

      {/* State header */}
      <div style={{ padding: '8px var(--gap)', borderBottom: '1px solid var(--line)', display: 'grid', gap: 6 }}>
        {agents.length > 0 && (
          <FormSelect value={agentId} onChange={(e) => setAgentId(e.target.value)} style={{ height: 32, fontSize: 'var(--t-xs)' }}>
            <option value="">בלי סוכן — זרימה בלבד</option>
            {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </FormSelect>
        )}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
          <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>שלב:</span>
          <StatusBadge tone="neutral" size="sm">{state.stage_key ?? '—'}</StatusBadge>
          {varEntries.map(([k, v]) => (
            <span key={k} dir="ltr" style={{ fontSize: 'var(--t-xs)', padding: '2px 6px', borderRadius: 'var(--r-pill)', background: 'var(--surface-2)', border: '1px solid var(--line)' }}>
              {k}={String(v)}
            </span>
          ))}
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} style={{ flex: 1, overflow: 'auto', padding: 'var(--gap)', display: 'flex', flexDirection: 'column', gap: 8, minHeight: 0 }}>
        {items.length === 0 && <p style={{ color: 'var(--muted)', fontSize: 'var(--t-xs)' }}>שלח הודעה כדי להתחיל לסמלץ את השיחה.</p>}
        {items.map((it, i) => it.kind === 'event' ? (
          <div key={i} style={{ alignSelf: 'center', fontSize: 'var(--t-xs)', color: 'var(--muted)', textAlign: 'center' }}>
            {eventLabel(it.event)}
          </div>
        ) : (
          <div
            key={i}
            style={{
              alignSelf: it.role === 'user' ? 'flex-start' : 'flex-end',
              maxWidth: '85%', padding: '6px 10px', borderRadius: 'var(--r-2)', fontSize: 'var(--t-sm)',
              background: it.role === 'user' ? 'var(--surface-2)' : 'var(--accent-soft, var(--surface-2))',
              border: `1px solid ${it.role === 'user' ? 'var(--line)' : 'var(--accent)'}`,
            }}
          >
            {it.text}
          </div>
        ))}
      </div>

      {/* Input */}
      <div style={{ padding: 'var(--gap)', borderTop: '1px solid var(--line)', display: 'flex', gap: 6 }}>
        <TextInput
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(); } }}
          placeholder="הקלד הודעה כלקוח…"
        />
        <button type="button" className="btn primary" onClick={send} disabled={sending || !input.trim()}>
          {sending ? '…' : 'שלח'}
        </button>
      </div>
    </div>
  );
}
